import React from "react";
import { Link } from "react-router-dom";
import { Bowtie } from "./icons";

/**
 * Homepage feature tile for the "Master SEO in 2026" premium course.
 *
 * Placed below the testimonials block so visitors who scrolled past the
 * primary funnel pitch get a second, focused conversion lever — the lowest-
 * priced premium product on the site ($197) doubles as a brand-affinity
 * lead magnet for higher-ticket Concierge plans.
 */
export default function SeoCourseFeatureTile() {
  return (
    <section
      data-testid="home-seo-course-tile"
      className="relative px-6 md:px-12 py-20 md:py-28"
      style={{
        background:
          "linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.04) 50%, transparent 100%)",
      }}
    >
      {/* top gold divider line */}
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.45), transparent)" }}
      />

      <div className="max-w-6xl mx-auto">
        <div
          className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center rounded-3xl border border-gold/25 bg-black/55 backdrop-blur-md px-6 md:px-10 lg:px-14 py-10 md:py-14 overflow-hidden"
          style={{ boxShadow: "0 0 80px rgba(212,175,55,0.18)" }}
        >
          {/* LEFT — copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold/85 px-3.5 py-1.5 rounded-full border border-gold/30 bg-black/50">
              <Bowtie className="w-3 h-3 text-gold" />
              New · Premium SEO Course
            </span>

            <h2
              data-testid="home-seo-course-tile-headline"
              className="mt-7 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.06]"
            >
              Master SEO <span className="bk-gold-text italic">in 2026.</span>
            </h2>
            <p className="mt-2 font-display text-lg md:text-xl text-white/75 italic">
              The Bow Tie Method.
            </p>

            <p className="mt-5 text-base md:text-[17px] text-white/65 leading-relaxed max-w-xl">
              The 10-blue-links era is over. <strong className="text-white">60%+ of searches end without a click.</strong> Learn how to rank in Google AI Overviews, ChatGPT citations, and Perplexity — with the only 5-pillar framework built for the AI-search era.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-white/75">
              {[
                "12 modules · 60+ lessons",
                "Premium Manual (PDF)",
                "30+ ready-to-ship templates",
                "Branded completion certificate",
              ].map((l, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="text-gold mt-0.5">→</span>
                  <span>{l}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <div>
                <span className="font-display text-4xl md:text-5xl text-white">$197</span>
                <span className="ml-2 text-[11px] uppercase tracking-[0.22em] text-white/45">one-time · lifetime</span>
              </div>
              <Link
                to="/seo-course"
                data-testid="home-seo-course-tile-cta"
                className="px-7 py-3.5 rounded-full text-[12px] uppercase tracking-[0.24em] font-bold text-black transition-all hover:scale-[1.02]"
                style={{
                  background: "linear-gradient(135deg, #F9E5C0 0%, #D4AF37 55%, #997A15 100%)",
                  boxShadow: "0 0 28px rgba(212,175,55,0.32)",
                }}
              >
                Explore the course →
              </Link>
            </div>
          </div>

          {/* RIGHT — branded course-box artwork */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <img
              src="/seo-course/hero-box.png"
              alt="Master SEO in 2026 — Premium Course + Manual"
              loading="lazy"
              data-testid="home-seo-course-tile-image"
              className="w-full h-auto"
              style={{ filter: "drop-shadow(0 25px 50px rgba(212,175,55,0.25))" }}
            />
          </div>
        </div>
      </div>

      {/* bottom gold divider line */}
      <div
        className="absolute inset-x-0 bottom-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.45), transparent)" }}
      />
    </section>
  );
}
